import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../utils/cn.js';

export interface PhoneInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'onChange' | 'value'> {
  /** Country code shown as an addon. Default `+91`. */
  countryCode?: string;
  /** Max digits allowed. Default 10 (India). */
  maxDigits?: number;
  /** Controlled value — digits only, no country code. */
  value?: string;
  /** Called with the digits-only string (already stripped and truncated). */
  onChange?: (digits: string) => void;
  /** Additional class on the wrapping `.input-group`. */
  wrapperClassName?: string;
}

/**
 * `<PhoneInput>` — India-first phone input with a static country-code
 * addon. Consolidates the six hand-rolled variants across RegisterStudent,
 * AcceptInvite, Contact, etc.
 *
 * - Strips non-digits automatically.
 * - Truncates to `maxDigits` (default 10 for India).
 * - `autoComplete="tel-national"`, `inputMode="tel"`.
 * - Static `+91` addon — swap `countryCode` prop for other countries when
 *   internationalisation is a real need.
 * - `enterKeyHint="next"` so mobile keyboards show "Next" not "Go".
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    countryCode = '+91',
    maxDigits = 10,
    value,
    onChange,
    wrapperClassName,
    className,
    placeholder = '10 digits',
    ...rest
  },
  ref,
) {
  return (
    <div className={cn('input-group', wrapperClassName)}>
      <span className="input-addon" aria-hidden="true">
        {countryCode}
      </span>
      <input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        enterKeyHint="next"
        maxLength={maxDigits}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, maxDigits);
          onChange?.(digits);
        }}
        className={cn('input', className)}
        {...rest}
      />
    </div>
  );
});
